import React, { useMemo, useState, useContext } from 'react';
import { Stack, Box, Link, Typography, useMediaQuery } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { alpha } from '@mui/material/styles';
import { formatNumberToCurrency } from '@/utils/currency';
import { AccountBase } from '@/models/AccountViewModel';
import {
  AccountContentProps,
  AccountType,
  DataField as DataFieldType,
  DynamicField,
} from '../types';
import {
  FLYOUT_CONTENT_PADDING_X,
  FLYOUT_CONTENT_PADDING_Y,
  FLYOUT_CONTENT_GAP,
} from '../constants';
import AccountTabs from './AccountTabs';
import DataField from '../common/DataField';
import { useLocalization } from '@/hooks/useLocalization';
import { useModuleAccess } from '@/permissions';
import { mobileWidth } from '@/constants';
import BannerList, { BannerItem } from '@/components/common/BannerList';
import { SyncErrorIcon } from '@/components/connectionError/SyncErrorIcon';
import { useAccountConnectionError } from '@/hooks/useAccountConnectionError';
import { ConnectionErrorModal } from '@/components/connectionError/ConnectionErrorModal';
import { HelpSupportContext } from '@/contexts/helpSupportContext';
import { formatAppDate } from '@/utils/date';
import { useAdvisorAccountFields } from '@/modules/advisor/hooks/useAdvisorAccountFields';

const accountTypes: Partial<Record<number, AccountType>> = {
  1: {
    name: 'Linked Accounts',
    description:
      'All accounts that were added through your login information with that institution.',
  },
  2: {
    name: 'Manually Entered Accounts',
    description:
      'All accounts that were added manually and are not accessed online.',
  },
  3: {
    name: 'Estate Accounts',
    description:
      'All accounts that were added to the estate and then allocated to you.',
  },
  4: {
    name: 'Entities',
    description:
      'All entities that were added to the estate and then allocated to you.',
  },
  8: {
    name: 'Managed By Advisor',
    description: 'All accounts that your advisor added to the system for you.',
  },
};

const formatClassification = (account: AccountBase): string => {
  if (!account.ClassificationName) {
    return 'Not set';
  }

  let classification = account.ClassificationName;

  if (
    account.SubClassificationName &&
    account.SubClassificationName.toLowerCase() !== 'not set'
  ) {
    classification += ` / ${account.SubClassificationName}`;
  }

  return classification;
};

const AccountContent: React.FC<AccountContentProps> = ({
  account,
  dynamicFields = [],
  tabsPanelKey,
  initialTabId,
  investorId,
  showClosedAccountBanner = false,
}) => {
  const { t } = useLocalization();
  const isMobile = useMediaQuery(mobileWidth);
  const { validateByModule, validateByFeature } = useModuleAccess();
  // FCB custom fields (FUE-172): gated by per-firm "Feature" restrictions.
  // Off by default; the backend omits the restriction only for enabled firms.
  const accountsModuleEnabled = validateByModule('Accounts');
  const showAvailableBalance =
    accountsModuleEnabled && validateByFeature('AvailableBalance');
  const showLinkedAccount =
    accountsModuleEnabled && validateByFeature('LinkedAccount');
  const connectionError = useAccountConnectionError(account.InstitutionId);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const { handleOpen: openHelp } = useContext(HelpSupportContext);
  const advisorFields = useAdvisorAccountFields(account);
  const closedAccountBannerItems = useMemo<BannerItem[]>(() => {
    if (!showClosedAccountBanner || !account.ClosedDate) {
      return [];
    }

    return [
      {
        id: 'closed-account-banner',
        icon: <InfoOutlinedIcon fontSize='small' />,
        content: (
          <Typography component='span'>
            {t('closedAccounts.closedOn', 'Closed on')}{' '}
            {formatAppDate(account.ClosedDate)}
          </Typography>
        ),
      },
    ];
  }, [account.ClosedDate, showClosedAccountBanner, t]);

  const data = useMemo(() => {
    const accountNumber = (account.AccountNumber ?? '').trim();
    const accountUrl = (account.AccountUrl ?? '').trim();
    const accountNumberValue =
      !isMobile && accountNumber && accountUrl ? (
        <Link
          href={accountUrl}
          target='_blank'
          rel='noopener noreferrer'
          underline='hover'
        >
          {accountNumber}
        </Link>
      ) : (
        accountNumber
      );

    const fields: DataFieldType[] = [
      {
        title: t('grid.balance'),
        value: formatNumberToCurrency(account.Balance),
      },
      ...(showAvailableBalance && account.AvailableBalance != null
        ? [
            {
              title: t('grid.availableBalance'),
              value: formatNumberToCurrency(account.AvailableBalance),
            },
          ]
        : []),
      { title: t('grid.accountNumber'), value: accountNumberValue },
      {
        title: t('grid.lastUpdated'),
        value: formatAppDate(account.LastUpdated),
        tooltip: t('tooltip.lastUpdated.content'),
      },
      { title: t('grid.accountType'), value: formatClassification(account) },
      ...advisorFields,
      {
        title: t('grid.dataSource'),
        value: accountTypes[account.AccountType]?.name || 'N/A',
      },
    ];

    // Add grouped custom fields if any exist
    if (dynamicFields.length > 0) {
      const customValues = dynamicFields
        .map((field: DynamicField) => field.value)
        .filter((value) => value !== '')
        .join(', ');

      if (customValues) {
        fields.push({ title: 'Custom Account Groups', value: customValues });
      }
    }

    if (showLinkedAccount && account.LinkedAccountNumber) {
      fields.push({
        title: t('grid.linkedAccount'),
        value: account.LinkedAccountNumber,
      });
    }

    return fields;
  }, [
    account,
    dynamicFields,
    showAvailableBalance,
    showLinkedAccount,
    isMobile,
    advisorFields,
    t,
  ]);

  return (
    <Stack
      direction='column'
      px={FLYOUT_CONTENT_PADDING_X}
      py={FLYOUT_CONTENT_PADDING_Y}
      gap={FLYOUT_CONTENT_GAP}
    >
      <BannerList
        items={closedAccountBannerItems}
        containerSx={{ width: '100%' }}
        alertSx={{
          backgroundColor: (theme) =>
            `${theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.12) : alpha(theme.palette.common.black, 0.04)} !important`,
          backgroundImage: 'none !important',
          border: (theme) =>
            `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.18) : alpha(theme.palette.common.black, 0.08)} !important`,
          color: (theme) => theme.palette.primary.main,
        }}
      />
      <Box
        display='flex'
        flexWrap='wrap'
        mx={-1}
      >
        {data.map(({ title, value, tooltip }, index) => (
          <DataField
            key={index}
            title={title}
            value={value}
            tooltip={tooltip}
          />
        ))}
      </Box>

      {connectionError && (
        <>
          <Stack
            direction='row'
            alignItems='center'
            gap={0.5}
          >
            <SyncErrorIcon />
            <Link
              component='button'
              type='button'
              variant='body2'
              color='error'
              onClick={() => {
                setErrorModalOpen(true);
              }}
              sx={{ textAlign: 'left' }}
            >
              {t('connectionError.label')}
            </Link>
          </Stack>
          <ConnectionErrorModal
            open={errorModalOpen}
            onClose={() => {
              setErrorModalOpen(false);
            }}
            institutionName={account.InstitutionName}
            connectionError={connectionError}
            onContactSupport={() => {
              setErrorModalOpen(false);
              openHelp(true, 'account connection');
            }}
          />
        </>
      )}

      <AccountTabs
        account={account}
        tabsPanelKey={tabsPanelKey}
        initialTabId={initialTabId}
        investorId={investorId}
      />
    </Stack>
  );
};

export default React.memo(AccountContent);
